import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { UiSettings } from '@/modules/shared/types';
import { colorTokenToCss, colorTokenToHslComponents } from '@/modules/shared/utils/colorTokens';

interface AppThemeContextType {
  settings: UiSettings | null;
  isLoading: boolean;
  error: string | null;
  refreshSettings: () => Promise<void>;
  applyTheme: () => void;
}

const AppThemeContext = createContext<AppThemeContextType | undefined>(undefined);

// Default UI settings
const DEFAULT_APP_UI_SETTINGS: UiSettings = {
  tabText: 'EPPREM',
  loginPageLogoUrl: '',
  loginPageLogoS3Key: '',
  insidePageLogoUrl: '',
  insidePageLogoS3Key: '',
  faviconUrl: '',
  faviconS3Key: '',
  primaryFontFamily: 'Inter, system-ui, sans-serif',
  secondaryFontFamily: 'Inter, system-ui, sans-serif',
  primaryColor: 'primary',
  secondaryColor: 'textMuted',
  accentColor: 'warning',
  successColor: 'success',
  warningColor: 'warning',
  errorColor: 'danger',
  backgroundColor: 'background',
  surfaceColor: 'card',
  textPrimaryColor: 'textPrimary',
  textSecondaryColor: 'textMuted',
  darkModeEnabled: false,
  showPageInstructions: false,
  isActive: true,
};

// Apply global theme settings to the document
function applyGlobalTheme(settings: UiSettings | null) {
  const root = document.documentElement;
  const activeSettings = settings || DEFAULT_APP_UI_SETTINGS;
  
  // Apply tab title
  if (activeSettings.tabText) {
    document.title = activeSettings.tabText;
  }
  
  // Apply favicon
  if (activeSettings.faviconUrl) {
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = activeSettings.faviconUrl;
  }
  
  // Apply color theme CSS custom properties
  if (activeSettings.primaryColor) {
    root.style.setProperty('--app-primary', colorTokenToCss(activeSettings.primaryColor, 'primary'));
    root.style.setProperty('--primary', colorTokenToHslComponents(activeSettings.primaryColor, 'primary', root));
  }
  if (activeSettings.secondaryColor) {
    root.style.setProperty('--app-secondary', colorTokenToCss(activeSettings.secondaryColor, 'textMuted'));
  }
  if (activeSettings.accentColor) {
    root.style.setProperty('--app-accent', colorTokenToCss(activeSettings.accentColor, 'warning'));
  }
  if (activeSettings.successColor) {
    root.style.setProperty('--app-success', colorTokenToCss(activeSettings.successColor, 'success'));
  }
  if (activeSettings.warningColor) {
    root.style.setProperty('--app-warning', colorTokenToCss(activeSettings.warningColor, 'warning'));
  }
  if (activeSettings.errorColor) {
    root.style.setProperty('--app-error', colorTokenToCss(activeSettings.errorColor, 'danger'));
  }
  if (activeSettings.backgroundColor) {
    root.style.setProperty('--app-background', colorTokenToCss(activeSettings.backgroundColor, 'background'));
  }
  if (activeSettings.surfaceColor) {
    root.style.setProperty('--app-surface', colorTokenToCss(activeSettings.surfaceColor, 'card'));
  }
  if (activeSettings.textPrimaryColor) {
    root.style.setProperty('--app-text-primary', colorTokenToCss(activeSettings.textPrimaryColor, 'textPrimary'));
  }
  if (activeSettings.textSecondaryColor) {
    root.style.setProperty('--app-text-secondary', colorTokenToCss(activeSettings.textSecondaryColor, 'textMuted'));
  }
  if (activeSettings.primaryFontFamily) {
    root.style.setProperty('--app-font-primary', activeSettings.primaryFontFamily);
  }
  if (activeSettings.secondaryFontFamily) {
    root.style.setProperty('--app-font-secondary', activeSettings.secondaryFontFamily);
  }
}

interface AppThemeProviderProps {
  children: ReactNode;
}

const THEME_CACHE_KEY = 'epprem-app-theme';

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const [settings, setSettings] = useState<UiSettings | null>(() => {
    try {
      const cached = localStorage.getItem(THEME_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached) as UiSettings;
        // Apply immediately to prevent flicker
        applyGlobalTheme(parsed);
        return parsed;
      }
    } catch { /* ignore */ }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Use dynamic import to avoid circular dependency
      const { applicationSettingService } = await import('@/modules/superadmin/services/applicationSettingService');
      const response = await applicationSettingService.getUiSettings();
      
      if (response.success && response.data) {
        setSettings(response.data);
        applyGlobalTheme(response.data);
        // Cache for instant load next time
        try { localStorage.setItem(THEME_CACHE_KEY, JSON.stringify(response.data)); } catch { /* ignore */ }
      } else {
        // No custom settings - use defaults
        setSettings(null);
        applyGlobalTheme(null);
      }
    } catch (err) {
      console.error('Failed to fetch app UI settings:', err);
      setError('Failed to load app theme');
      // Still apply default theme on error
      applyGlobalTheme(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const refreshSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  const applyTheme = useCallback(() => {
    applyGlobalTheme(settings);
  }, [settings]);

  return (
    <AppThemeContext.Provider
      value={{
        settings,
        isLoading,
        error,
        refreshSettings,
        applyTheme,
      }}
    >
      {children}
    </AppThemeContext.Provider>
  );
}

export function useAppTheme() {
  const context = useContext(AppThemeContext);
  if (context === undefined) {
    throw new Error('useAppTheme must be used within an AppThemeProvider');
  }
  return context;
}

// Hook for accessing theme settings with defaults
export function useAppThemeSettings(): UiSettings {
  const { settings } = useAppTheme();
  
  return {
    ...DEFAULT_APP_UI_SETTINGS,
    ...settings,
  };
}
